import { BCMSDate, BCMSRecord, DisabledPrompt, Link, useBCMSContext, useGlobalController } from "@bcm-dev/bcms-vite";
import { FormContainer, FormRow, FormRowView } from "components/shared";
import { PrimaryButton, SecondaryButton } from "components/ui";
import { UITextClearInput } from "components/ui/UITextInput";
import Classes, { Account, CheckingAccount, LOCAccount } from "model";
import React, { useState } from "react";

import "./styles.css";

/**
 * @function AccountDetailsView
 * @return {React.Component}
 * @param {STRING} accountNickname - Sets the account nickname. 
 * @param {STRING} accountName - Sets the account name.
 * @param {STRING} accountNumber - Sets the account number. 
 * @param {STRING} routingNumber - Sets the routing number. 
 * @param {STRING} dateOpened - Sets the open date. 
 * @param {STRING} interestRate - Sets the interest rate. 
 * @param {STRING} yearToDateInterest - Sets the year-to-date interest. 
 * @param {STRING} lastDepositDate - Sets the last deposit date. 
 * @param {STRING} lastDeposit - Sets the last deposit amount. 
 * @param {STRING} lastInterestPaidDate - Sets the last interest paid date. 
 * @param {STRING} lastInterestAmount - Sets the last interest amount. 
 * @exported true
 * @description The main component for the AccountDetailsView. This component is shared between MobileAccountDetailsModal and AccountDetailsModal.
 */
export default function AccountDetailsView(props) {
    // Initalize globalController and set the 'checkingAccount' variable to 'props.data'
    const globalController = useGlobalController();
    const bcmsContext = useBCMSContext();

    const selectedAccount = props.data;
    const accountType = selectedAccount.type;
    const isCheckingAccount = accountType === "checking";

    // Define the default state and initalize state for the nickname field
    const defaultState = { "show": false, "value": selectedAccount.nickname };
    const [nickname, setNickname] = useState(defaultState);

    // cancel :: set the nickname state back to default
    const cancel = evt => setNickname(defaultState);
    // On save update the Account and CheckingAccount classes and batch
    // changes to global model then update state and close the nickname field view
    const save = evt => {
        const updateAccount = Account.initByID(selectedAccount.id);
        const updateNickname = nickname.value !== "" ? nickname.value :  selectedAccount.name;
        
        updateAccount.nickname = updateNickname;
        selectedAccount.nickname = updateNickname;

        BCMSRecord.batch([
            Account.update(updateAccount)
        ], bcmsContext, Classes);

        BCMSRecord.batch([
            Account.update(updateAccount),
            isCheckingAccount ? CheckingAccount.update(selectedAccount) : LOCAccount.update(selectedAccount)
        ], globalController);

        setNickname({ ...nickname, "show": false });
    };

    const formRowProps = { selectedAccount, nickname, setNickname, cancel, save };

    return (
        <div className="account-details-view">
            <FormContainer>
                { isCheckingAccount ? <CheckingAccountFormRows { ...formRowProps } /> : <LOCAccountFormRows { ...formRowProps } /> }
            </FormContainer>
        </div>
    )
}

function CheckingAccountFormRows(props) {
    const selectedAccount = props.selectedAccount ?? {};
    const nickname = props.nickname ?? "";
    const setNickname = props.setNickname ?? (() => {});
    const cancel = props.cancel  ?? (() => {});;
    const save = props.save  ?? (() => {});;

    return (
        <>
            <FormRowView text="Account Nickname" className="account-nickname-section">
                {!nickname.show && <b>{selectedAccount.nickname}</b>}

                {nickname.show &&
                    <div className="input-container">
                        <UITextClearInput value={nickname.value} onChange={value => setNickname({ ...nickname, "value": value })} />
                        <div className="flex-container flex-horizontal align-center button-container">
                            <SecondaryButton text="Cancel" onClick={cancel} />
                            <PrimaryButton text="Save" onClick={save} />
                        </div>
                    </div>}
            </FormRowView>
            { !nickname.show &&
            <FormRow className="change-account-nickname-link">
                <Link onClick={() => setNickname({ ...nickname, "show": true })}><span data-point="Nickname">Change Account Nickname</span></Link>
            </FormRow>
            }
            <FormRowView text="Account Name" className="account-name-section">
                <b>{selectedAccount.name}</b>
            </FormRowView>
            <FormRowView text="Account Number" className="account-number-section">
                <b>{selectedAccount.number}</b>
            </FormRowView>
            <FormRow className="show-hide-full-account-number-link">
                <DisabledPrompt>
                    <Link to="/" dataPoint="Show_Full_Account_Number_Link">Show Full Account Number</Link>
                </DisabledPrompt>
            </FormRow>
            <FormRowView text="Routing Number" className="routing-number-section">
                <b>123456789</b>
            </FormRowView>
            <FormRowView text="Date Opened" className="date-opened-section">
                <b>{selectedAccount.dateOpened.format("m/d/Y")}</b>
            </FormRowView>
            <FormRowView text="Interest Rate" className="interest-rate-section interest-section" data-point="InterestRate">
                <b>{selectedAccount.interestRate}%</b>
            </FormRowView>
            <FormRowView text="Year To Date Interest" className="ytd-interest-section interest-section">
                <b>${selectedAccount.ytdInterest}</b>
            </FormRowView>
            <FormRowView text="Last Deposit Date" className="last-deposit-date-section">
                <b>{BCMSDate.init().offset(-20).format("m/d/Y")}</b>
            </FormRowView>
            <FormRowView text="Last Deposit" className="last-deposit-section">
                <b>$1,000</b>
            </FormRowView>
            <FormRowView text="Last Interest Paid Date" className="last-interest-paid-date-section interest-section">
                <b>{BCMSDate.init().offset(-10).format("m/d/Y")}</b>
            </FormRowView>
            <FormRowView text="Last Interest Amount" className="last-interest-amount-section interest-section">
                <b>$100</b>
            </FormRowView>
        </>
    );
}

function LOCAccountFormRows(props) {
    const selectedAccount = props.selectedAccount ?? {};
    const nickname = props.nickname ?? "";
    const setNickname = props.setNickname ?? (() => {});
    const cancel = props.cancel  ?? (() => {});;
    const save = props.save  ?? (() => {});;

    return (
        <>
            <FormRowView text="Account Nickname" className="account-nickname-section">
                {!nickname.show && <b>{selectedAccount.nickname}</b>}

                {nickname.show &&
                    <div className="input-container">
                        <UITextClearInput value={nickname.value} onChange={value => setNickname({ ...nickname, "value": value })} />
                        <div className="flex-container flex-horizontal align-center button-container">
                            <SecondaryButton text="Cancel" onClick={cancel} />
                            <PrimaryButton text="Save" onClick={save} />
                        </div>
                    </div>}
            </FormRowView>
            { !nickname.show &&
            <FormRow className="change-account-nickname-link">
                <Link onClick={() => setNickname({ ...nickname, "show": true })}><span data-point="Nickname">Change Account Nickname</span></Link>
            </FormRow>
            }
            <FormRowView text="Account Name" className="account-name-section">
                <b>{selectedAccount.name}</b>
            </FormRowView>
            <FormRowView text="Account Number" className="account-number-section">
                <b>{selectedAccount.number}</b>
            </FormRowView>
            <FormRowView text="Date Opened" className="date-opened-section">
                <b>{selectedAccount.dateOpened.format("m/d/Y")}</b>
            </FormRowView>
            <FormRowView text="Interest Rate" className="interest-rate-section" data-point="InterestRate">
                <b>{selectedAccount.interestRate}%</b>
            </FormRowView>
            <FormRowView text="Payment Due Date" className="last-deposit-date-section">
                <b>{selectedAccount.dueDate.format("m/d/Y")}</b>
            </FormRowView>
            <FormRowView text="Amount Due" className="last-deposit-date-section">
                <b>{ selectedAccount.due.format() }</b>
            </FormRowView>
            <FormRowView text="Maturity Date" className="last-deposit-date-section">
                <b>{ selectedAccount.maturityDate.format("m/d/Y") }</b>
            </FormRowView>
            <FormRowView text="Interest Rate Reprice Date" className="last-deposit-date-section">
                <b>{ selectedAccount.interestRateRepriceDate.format("m/d/Y") }</b>
            </FormRowView>
            <FormRowView text="Interest Rate PY" className="last-deposit-date-section">
                <b>{ `$${ selectedAccount.interestPaidPY }` }</b>
            </FormRowView>
            <FormRowView text="Routing Number" className="routing-number-section">
                <b>{ selectedAccount.routingNumber }</b>
            </FormRowView>
        </>
    );
}